import { useEffect, useState } from 'react';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  loading?: boolean;
  /** 이 값을 설정하면, 사용자가 이 문자열을 직접 입력해야만 확인 버튼이 활성화돼요. */
  dangerConfirmText?: string;
  onCancel: () => void;
  onConfirm: () => void;
};

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = '삭제',
  loading = false,
  dangerConfirmText,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  // 다이얼로그가 열릴 때마다 입력값 초기화
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  if (!open) return null;

  const needsTyping = !!dangerConfirmText;
  const confirmed = !needsTyping || typed.trim() === dangerConfirmText;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40" onClick={onCancel}></div>
      <div className="relative w-full max-w-sm rounded-xl bg-white p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-alert-line text-xl"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-foreground-950">{title}</h3>
            <p className="mt-1 text-sm text-foreground-600">{message}</p>
          </div>
        </div>

        {needsTyping && (
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium text-foreground-700">
              확인을 위해 학원 이름을 정확히 입력해 주세요.
            </p>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={dangerConfirmText}
              autoComplete="off"
              className="w-full rounded-md border border-accent-300 px-3 py-2 text-sm text-foreground-900 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-200"
            />
            {typed.trim() !== dangerConfirmText && typed.length > 0 && (
              <p className="mt-1 text-xs text-accent-600">입력한 이름이 일치하지 않아요.</p>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || !confirmed}
            className="cursor-pointer whitespace-nowrap rounded-md bg-accent-600 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? '처리 중...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}